import { useNavigate } from 'react-router-dom';

import { AppIcon, SIDEBAR_COLLAPSED_WIDTH } from '@backrs_ui/common-ui';

import FallbackBanner from '@backrs_ui/common-ui/src/assets/images/profile-header-fallback.png';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  ProfileFields,
  ProfileValueOption,
  UserProfileResponse,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { BACKRS_APP_URL } from '@backrs_ui/grow-ui/src/settings';

import { Avatar, Stack, Theme, SxProps, Typography, Box, Button } from '@mui/material';

import { useGetSelectedOptions } from '../hooks/useGetSelectedOptions';

const PROFILE_HEADER_STRINGS = appStrings.modules.profile.public.header;

const ENROLLMENT_TYPES = {
  HIGH_SCHOOL: 'High School',
  TRADE_TECHNICAL_SCHOOL: 'Trade/Technical School',
  COMMUNITY_COLLEGE: 'Community College',
  COLLEGE_UNIVERSITY: 'College/University',
  GRADUATE_SCHOOL: 'Graduate School',
  NOT_CURRENTLY_A_STUDENT: 'I am not currently a student',
} as const;

const determineGradeLevel = (
  academicExpectedGraduationYear: number | undefined,
  academicEnrollmentType: string,
): string | undefined => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0 = Jan, 11 = Dec

  // If it's July or later, bump the academic year
  const effectiveAcademicYear = currentMonth >= 6 ? currentYear + 1 : currentYear;
  const yearsUntilGraduation = academicExpectedGraduationYear
    ? academicExpectedGraduationYear - effectiveAcademicYear
    : 0;

  if (!academicEnrollmentType && !!academicExpectedGraduationYear) {
    return academicExpectedGraduationYear.toString();
  }

  const graduationYear = academicExpectedGraduationYear
    ? ` (${academicExpectedGraduationYear})`
    : '';

  if (
    academicEnrollmentType === ENROLLMENT_TYPES.HIGH_SCHOOL ||
    academicEnrollmentType === ENROLLMENT_TYPES.COLLEGE_UNIVERSITY
  ) {
    switch (yearsUntilGraduation) {
      case 0:
        return `Senior${graduationYear}`;
      case 1:
        return `Junior${graduationYear}`;
      case 2:
        return `Sophomore${graduationYear}`;
      default:
        return `Freshman${graduationYear}`;
    }
  }

  if (academicEnrollmentType === ENROLLMENT_TYPES.NOT_CURRENTLY_A_STUDENT) {
    return 'Not currently a student';
  }

  return `${academicEnrollmentType}${graduationYear}`;
};

type PublicProfileHeaderProps = Readonly<{
  publicProfile?: UserProfileResponse;
  loggedInUserId?: number;
}>;

export const PublicProfileHeader = ({
  publicProfile,
  loggedInUserId,
}: PublicProfileHeaderProps) => {
  const navigate = useNavigate();

  const selectedOptions = useGetSelectedOptions({
    userProfile: publicProfile,
    isEditView: false,
  });

  const { first_name, last_name, profile_image, city, state } =
    publicProfile?.profile?.publicProfile ?? {};

  const formatCityState = () => {
    const location = [city, state].filter(Boolean);

    return location.length > 0 ? location.join(', ') : undefined;
  };

  const findStringValue = (fieldKey: ProfileFields): string => {
    const filteredOptions = selectedOptions(fieldKey).filter(
      (opt): opt is ProfileValueOption | string => opt !== undefined,
    );

    if (typeof filteredOptions[0] === 'object' && 'name' in filteredOptions[0]) {
      return filteredOptions[0].name;
    }

    return filteredOptions[0]?.toString() || '';
  };

  const isStudent = publicProfile?.profile?.publicProfile?.role === 'student';
  const isOwnProfile =
    loggedInUserId?.toString() === publicProfile?.profile?.publicProfile?.id?.toString();

  const handleBackClick = () => {
    if (isOwnProfile) {
      navigate(APP_ROUTE.PROFILE);
    } else {
      window.open(
        `${BACKRS_APP_URL}/profile/${publicProfile?.profile?.publicProfile.id}`,
        '_blank',
      );
    }
  };

  const doesStudentHaveEnrollmentOrGraduationYear =
    isStudent &&
    (findStringValue('academicsHighSchoolCurrentEnrollment') ||
      findStringValue('academicEnrollmentTypes') ||
      publicProfile?.profile?.academicHighSchoolExpectedGraduationYear);

  return (
    <Stack sx={containerSx}>
      <Box
        component="img"
        src={publicProfile?.profile?.publicProfile?.profile_banner_image || FallbackBanner}
        sx={backgroundHeaderImageSx}
      />

      <Stack sx={contentContainerSx}>
        <Box sx={avatarContainerSx}>
          <Avatar src={profile_image} sx={avatarSx} />
          <Box sx={roleSx}>
            <Typography variant="display-xs-semibold" sx={roleTextSx}>
              {isStudent ? 'S' : 'B'}
            </Typography>
          </Box>
        </Box>

        <Box sx={nameInfoContainerSx}>
          <Box sx={nameActionsContainerSx}>
            <Typography sx={nameSx}>
              {first_name} {last_name}
            </Typography>

            <Box sx={actionsContainerSx}>
              <Button
                variant="contained"
                size="md"
                hierarchy="Secondary gray"
                sx={messageButtonSx}
                disabled={isOwnProfile}
                onClick={() => {
                  window.open(
                    `${BACKRS_APP_URL}/profile/${publicProfile?.profile?.publicProfile.id}`,
                    '_blank',
                  );
                }}
              >
                {PROFILE_HEADER_STRINGS.message}
              </Button>

              <Button variant="contained" size="md" hierarchy="Primary" onClick={handleBackClick}>
                {isOwnProfile ? PROFILE_HEADER_STRINGS.edit : PROFILE_HEADER_STRINGS.back}
              </Button>
            </Box>
          </Box>

          <Box sx={textItemsContainerSx}>
            {(doesStudentHaveEnrollmentOrGraduationYear || !isStudent) && (
              <Box sx={textItemsBoxSx}>
                {/* ACADEMIC GRADE (if student) */}
                {determineGradeLevel(
                  publicProfile?.profile?.academicHighSchoolExpectedGraduationYear,
                  findStringValue('academicEnrollmentTypes'),
                ) &&
                  isStudent && (
                    <Typography variant="text-sm-medium" sx={textItemSx}>
                      <AppIcon
                        iconName="Clock"
                        diameter="16px"
                        color="Colors/Border/border-brand"
                      />
                      {determineGradeLevel(
                        publicProfile?.profile?.academicHighSchoolExpectedGraduationYear,
                        findStringValue('academicEnrollmentTypes'),
                      )}
                    </Typography>
                  )}

                {/* CAREER OCCUPATION (if professional) */}
                {publicProfile?.profile?.careerOccupation && !isStudent && (
                  <Typography variant="text-sm-medium" sx={textItemSx}>
                    <AppIcon iconName="Clock" diameter="16px" color="Colors/Border/border-brand" />
                    {publicProfile.profile.careerOccupation}
                  </Typography>
                )}

                {/* CURRENT ENROLLMENT (if student) */}
                {findStringValue('academicsHighSchoolCurrentEnrollment') &&
                  isStudent &&
                  findStringValue('academicEnrollmentTypes') !==
                    ENROLLMENT_TYPES.NOT_CURRENTLY_A_STUDENT && (
                    <Typography variant="text-sm-medium" sx={textItemSx}>
                      <AppIcon iconName="Bank" diameter="16px" color="Colors/Border/border-brand" />
                      {findStringValue(
                        findStringValue('academicEnrollmentTypes') === ENROLLMENT_TYPES.HIGH_SCHOOL
                          ? 'academicsHighSchoolCurrentEnrollment'
                          : 'academicsPostSecondaryCurrentEnrollment',
                      )}
                    </Typography>
                  )}

                {/* CAREER EMPLOYER (if professional) */}
                {publicProfile?.profile?.careerEmployer?.[0] && !isStudent && (
                  <Typography variant="text-sm-medium" sx={textItemSx}>
                    <AppIcon
                      iconName="Certificate01"
                      diameter="16px"
                      color="Colors/Border/border-brand"
                    />
                    {publicProfile.profile.careerEmployer[0]}
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={textItemsBoxSx}>
              {/* LOCATION */}
              {formatCityState() && (
                <Typography variant="text-sm-medium" sx={textItemSx}>
                  <AppIcon
                    iconName="MarkerPin01"
                    diameter="16px"
                    color="Colors/Border/border-brand"
                  />
                  {formatCityState()}
                </Typography>
              )}

              {/* MAJOR (if student) */}
              {findStringValue('academicMajors') && isStudent && (
                <Typography variant="text-sm-medium" sx={textItemSx}>
                  <AppIcon
                    iconName="Certificate01"
                    diameter="16px"
                    color="Colors/Border/border-brand"
                  />
                  {findStringValue('academicMajors')}
                </Typography>
              )}

              {/* ALMA MATER (if professional) */}
              {findStringValue('academicsAlmaMaters') && !isStudent && (
                <Typography variant="text-sm-medium" sx={textItemSx}>
                  <AppIcon iconName="Bank" diameter="16px" color="Colors/Border/border-brand" />
                  {findStringValue('academicsAlmaMaters')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'flex-start', sm: 'center' },
  paddingBottom: backrs.spacing['spacing-4xl'],
  position: 'relative',
  paddingTop: {
    xs: 'calc(120px / 1.25)',
    md: 'calc(160px / 1.25)',
  },
  paddingX: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
  gap: { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-2xl'] },
  flexWrap: { xs: 'wrap' },
});

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'flex-start', sm: 'center' },
  position: 'relative',
  margin: '0 auto',
  maxWidth: '1216px',

  '@media (max-width: 1366px) and (max-height: 768px) and (min-width: 405px)': {
    maxWidth: '100%',
    marginLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
  },
  '@media (max-width: 900px) and (max-height: 504px)': {
    maxWidth: '100%',
    marginLeft: '0px',
  },

  paddingBottom: {
    xs: '0 !important',
    sm: backrs.spacing['spacing-4xl'],
  },
  paddingTop: 0,
  paddingLeft: {
    xs: '0 !important',
    sm: backrs.spacing['spacing-4xl'],
  },
  paddingRight: {
    xs: '0 !important',
    sm: backrs.spacing['spacing-4xl'],
  },
  gap: { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-2xl'] },
  flexWrap: { xs: 'wrap' },
});

const backgroundHeaderImageSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: {
    xs: '120px',
    md: '160px',
  },
  objectFit: 'cover',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 0,
  borderBottom: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const nameSx: SxProps<Theme> = ({ typography, breakpoints }) => ({
  [breakpoints.down('md')]: {
    ...typography['display-xs-semibold'],
  },

  [breakpoints.up('md')]: {
    ...typography['display-sm-semibold'],
  },
});

const nameInfoContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  alignItems: 'flex-start',
  alignSelf: { xs: 'flex-start', sm: 'flex-end' },
  mb: backrs.spacing['spacing-md'],
  width: {
    xs: '100%',
    sm: `calc(100% - ${backrs.spacing['spacing-11xl']} - ${backrs.spacing['spacing-3xl']})`,
  },
  flexWrap: 'wrap',
});

const avatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: {
    xs: backrs.spacing['spacing-9xl'],
    sm: backrs.spacing['spacing-11xl'],
  },
  height: {
    xs: backrs.spacing['spacing-9xl'],
    sm: backrs.spacing['spacing-11xl'],
  },
  borderRadius: '50%',
  border: `2px solid ${backrs.colors['Beige/950']}`,
  overflow: 'hidden',
  '& .MuiAvatar-img': {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
  },
});

const avatarContainerSx: SxProps<Theme> = {
  position: 'relative',
};

const roleSx: SxProps<Theme> = ({ backrs }) => ({
  width: {
    xs: backrs.spacing['spacing-3xl'],
    sm: backrs.spacing['spacing-5xl'],
  },
  height: {
    xs: backrs.spacing['spacing-3xl'],
    sm: backrs.spacing['spacing-5xl'],
  },
  border: `2px solid ${backrs.colors['Beige/950']}`,
  borderRadius: '50%',
  backgroundColor: backrs.colors['Brand/800'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: backrs.colors['Colors/Base/White'],
  position: 'absolute',
  zIndex: 1,
  bottom: backrs.spacing['spacing-xxs'],
  right: backrs.spacing['spacing-md'],
  pb: backrs.spacing['spacing-xxs'],
});

const roleTextSx: SxProps<Theme> = ({ breakpoints }) => ({
  [breakpoints.down('sm')]: {
    fontSize: '16px',
  },
});

const textItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const textItemsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: { xs: backrs.spacing['spacing-xs'], sm: backrs.spacing['spacing-3xl'] },
  columnGap: { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-3xl'] },
  width: '100%',
  marginTop: backrs.spacing['spacing-xl'],
  flexWrap: { xs: 'wrap' },
});

const textItemsBoxSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const nameActionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
  flexWrap: 'wrap',
  marginTop: { sm: backrs.spacing['spacing-4xl'], md: backrs.spacing['spacing-none'] },
});

const actionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
  '& .MuiButton-root': {
    boxSizing: 'border-box',
    height: '40px',
  },
  flexWrap: 'wrap',
});

const messageButtonSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Beige/950']}`,
  boxSizing: 'border-box',
});
